import React, { useCallback, useContext, useEffect, useState } from "react";
import { formatAttributeValues, getAttributeValueDisplay } from "../../services/common/AttributeUtils";
import { Context } from "../../states/Store";
import useStyles from "../../style/js-style/containers/thirdparties/sheet/attributes/AttributeValuesDataTableStyle.js";
import {
  getDossierAttributes,
  getThirdpartyAttributes,
} from "../../services/edge/AttributesService";
import i18next from "i18next";
import { Tab, Tabs } from '@mui/material';
import { EANoData } from "../../components/common/EANoData";
import SkeletonAttributes from "../../components/common/skeleton/SkeletonAttributes";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "../../services/common/RolesUtils";
import { getThirdpartyCalculatedAttributes } from "../../services/edge/ThirdpartyService";
import { formatCalculationResult } from "services/common/CalculatorUtils";
import AttributeValues from './AttributeValues';
import { AttributeType } from "services/common/Constants";
import { getDossierCalculatedAttributes } from "services/edge/DossierService";

function AttributeValuesDataTable({
  id,
  bondType,
  hasAnyOfPrivilegesOnScopedResource,
  isEditionAuthorized = true,
  setExpanded = () => { },
}) {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [loading, setLoader] = useState(true);
  const [classicAttributesValues, setClassicAttributesValues] = useState([]);
  const [calculatedAttributesValues, setCalculatedAttributesValues] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState();
  const [tabIndex, setTabIndex] = React.useState(0);

  const { hasAnyOfPrivilegesOnPropertyId, hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  const hasOverridePrivilegeOnListMultiple = useCallback((attribute) => {
    if ([AttributeType.LIST_MULTIPLE].includes(attribute.type) && attribute.bondType === "thirdparty") {
      return hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY)
          && hasAnyOfPrivilegesOnPropertyId(attribute.attributeId, PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE);
    }
    return false;
  }, [hasAnyOfPrivileges, hasAnyOfPrivilegesOnPropertyId])

  const canViewAttribute = useCallback((attribute) => {
    let bCanView = false;

    switch (attribute.bondType) {
      case "thirdparty":
        if (attribute.type === "calculated") {
          bCanView = hasAnyOfPrivilegesOnScopedResource(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE) &&
            hasAnyOfPrivilegesOnPropertyId(attribute.calculationId, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE);
        } else {
          bCanView = hasOverridePrivilegeOnListMultiple(attribute) || (hasAnyOfPrivilegesOnScopedResource(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE)
            && hasAnyOfPrivilegesOnPropertyId(attribute.attributeId, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE));
        }

        break;

      case "dossier":
        if (attribute.type === "calculated") {
          bCanView = hasAnyOfPrivilegesOnScopedResource(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE) &&
            hasAnyOfPrivilegesOnPropertyId(attribute.calculationId, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE);
        } else {
          bCanView = hasAnyOfPrivilegesOnScopedResource(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE)
            && hasAnyOfPrivilegesOnPropertyId(attribute.attributeId, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE);
        }

        break;

      default:
        throw new Error("Wrong bond type");
    }

    return bCanView;
  }, [hasAnyOfPrivilegesOnPropertyId, hasAnyOfPrivilegesOnScopedResource, hasOverridePrivilegeOnListMultiple]);

  const canEditAttribute = useCallback((attribute) => {
    let bCanEdit = false;

    switch (attribute.bondType) {
      case "thirdparty":
        if (attribute.type === "calculated") {
          bCanEdit = false;
        } else {
          bCanEdit = isEditionAuthorized && (hasOverridePrivilegeOnListMultiple(attribute) || (hasAnyOfPrivilegesOnScopedResource(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE) &&
            hasAnyOfPrivilegesOnPropertyId(attribute.attributeId, PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE)));
        }

        break;

      case "dossier":
        if (attribute.type === "calculated") {
          bCanEdit = false;
        } else {
        bCanEdit = isEditionAuthorized && (hasAnyOfPrivilegesOnScopedResource(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE)
          && hasAnyOfPrivilegesOnPropertyId(attribute.attributeId, PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE));
        }
        break;

      default:
        throw new Error("Wrong bond type");
    }

    return bCanEdit;
  }, [hasAnyOfPrivilegesOnPropertyId, hasAnyOfPrivilegesOnScopedResource, hasOverridePrivilegeOnListMultiple, isEditionAuthorized]);

  // Fetch Attributes values
  const fetchAttributesValuesList = useCallback(async () => {
    setLoader(true);
    try {
      let manualAttributes;

      switch (bondType) {
        case "thirdparty":
          manualAttributes = await getThirdpartyAttributes(state.account.id, id, null);
          break;

        case "dossier":
          manualAttributes = await getDossierAttributes(state.account.id, id, null);
          break;

        default:
          throw new Error("Wrong bond type");
      }

      const mappedAttributes = manualAttributes?.content?.map((attribute) => ({
        ...attribute,
        id: attribute.label,
        shownValues: getAttributeValueDisplay(attribute.type, formatAttributeValues(attribute)),
      }));

      setClassicAttributesValues(mappedAttributes || []);
      setLoader(false);
    } catch (error) {
      setClassicAttributesValues([]);
      setLoader(false);
    }
  }, [id, state.account, bondType]);

  // Fetch Calculated Attributes values
  const fetchCalculatedAttributesValuesList = useCallback(async () => {
    
    setLoader(true);
    
    try {
      let calculatedAttributes;

      switch (bondType) {
        case "thirdparty":
          calculatedAttributes = await getThirdpartyCalculatedAttributes(
            state.account.id,
            id,
            dispatch
          );
          break;

        case "dossier":
          calculatedAttributes = await getDossierCalculatedAttributes(
            state.account.id,
            id,
            dispatch
          );
          break;

        default:
          throw new Error("Wrong bond type");
      }

      const mappedAttributes = calculatedAttributes?.content?.map((attribute) => {
        return {
          id: attribute.id,
          calculationId: attribute.calculationId,
          label: attribute.name,
          bondType: bondType,
          type: "calculated",
          creationDate: null,
          updateDate: attribute.resultLastUpdateDate,
          values: null,
          shownValues: formatCalculationResult({ resultType: attribute.resultType, resultValue: attribute.resultValue }),
          color: attribute.resultColorCode
        }
      });

      setCalculatedAttributesValues(mappedAttributes || []);
      setLoader(false);
    } catch (error) {
      setCalculatedAttributesValues([]);
      setLoader(false);
    }
  }, [id, state.account, bondType, dispatch]);

  useEffect(() => {
    if (state.account && id) {
      fetchAttributesValuesList();
      fetchCalculatedAttributesValuesList();
    }
  }, [id, state.account, fetchAttributesValuesList, fetchCalculatedAttributesValuesList]);

  useEffect(() => {
    const combined = [...classicAttributesValues, ...calculatedAttributesValues].sort((a, b) =>
      a.label.localeCompare(b.label)
    ).filter(canViewAttribute);
    setAttributes(combined);
  }, [classicAttributesValues, calculatedAttributesValues, canViewAttribute]);

  useEffect(() => {
    setExpanded(attributes.length);
    // eslint-disable-next-line
  }, [attributes]);

  const openEditDialog = (attributeValue) => {
    setSelectedAttribute(attributeValue);
  };

  function a11yProps(index) {
    return {
      id: `tp-risks-tab-${index}`,
      "aria-controls": `tp-risks-tabpanel-${index}`,
      value: index,
    };
  }

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const tabClassName = (index, privilege) =>
    classes.tab +
    " " +
    (!hasAnyOfPrivilegesOnScopedResource(privilege) ? classes.tabDisabled
      : tabIndex === index ? classes.selectedTab : classes.unselectedTab)


  return !loading && attributes.length > 0 ? (
    <div className={classes.mainRoot} style={{minHeight: bondType === "dossier" && "273px"}}>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        aria-label="dashboard tabs"
        orientation={"vertical"}
      >
        <Tab
          label={i18next.t("thirdpartySheet.entered")}
          {...a11yProps(0)}
          className={tabClassName(0, (bondType === "dossier" ? PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE : PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE))}
        />
        <Tab
          label={i18next.t("thirdpartySheet.calculated")}
          {...a11yProps(1)}
          className={tabClassName(1, bondType === "dossier" ? PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE : PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE)}
        />
      </Tabs>

      <div className={classes.root}>
        {/* Attributs saisis */}
        {tabIndex === 0 &&
          <AttributeValues
            attributes={attributes.filter(attribute => attribute.type !== "calculated")}
            openEditDialog={openEditDialog}
            canEditAttribute={canEditAttribute}
            bondType={bondType}
            selectedAttribute={selectedAttribute}
            setSelectedAttribute={setSelectedAttribute}
            id={id}
            fetchAttributeValuesList={fetchAttributesValuesList}
            onUpdateSuccess={fetchAttributesValuesList}
          />
        }

        {/*Attributs calculés */}
        {tabIndex === 1 &&
          <AttributeValues
            attributes={attributes.filter(attribute => attribute.type === "calculated")}
            openEditDialog={openEditDialog}
            canEditAttribute={canEditAttribute}
            bondType={bondType}
            selectedAttribute={selectedAttribute}
            setSelectedAttribute={setSelectedAttribute}
            id={id}
            fetchAttributeValuesList={fetchCalculatedAttributesValuesList}
          />
        }
      </div>
    </div>
  ) : loading
    ? <SkeletonAttributes type={bondType} />
    : (
      <div
        className={
          bondType === "dossier"
            ? classes.noDataRootDossier
            : classes.noDataRootThird
        }
      >
        <EANoData
          icon={"noAttributes-" + bondType}
          label={i18next.t("attributes.noAttribute", {
            type: i18next.t("attributes." + bondType),
          })}
        />
      </div>
    );
}
export default AttributeValuesDataTable;
